import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORIES, URGENCY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Lock, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export const urgencyBadge = (u) => ({
    low: "bg-slate-100 text-slate-700", medium: "bg-nhs-blue/10 text-nhs-blue",
    high: "bg-nhs-amber/20 text-[#8a6400]", critical: "bg-nhs-red/10 text-nhs-red",
}[u] || "bg-slate-100 text-slate-700");

export const statusBadge = (s) => ({
    new: "bg-slate-100 text-slate-700", triaged: "bg-nhs-blue/10 text-nhs-blue",
    in_progress: "bg-nhs-amber/20 text-[#8a6400]", mediation: "bg-nhs-teal/15 text-nhs-teal",
    resolved: "bg-green-100 text-green-700", closed: "bg-slate-200 text-slate-600",
}[s] || "bg-slate-100 text-slate-700");

export default function SupportRequests() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isMgr = ["hr_leadership", "admin"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all");
    const [form, setForm] = useState({ subject: "", category: CATEGORIES[0], description: "", urgency: "medium", contact_method: "secure_chat", confidential: true });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const load = () => api.get("/support-requests").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.subject || !form.description) { toast.error("Subject and description are required"); return; }
        try {
            await api.post("/support-requests", form);
            toast.success("Support request submitted — you'll be triaged within 24–72 hours");
            setOpen(false);
            setForm({ subject: "", category: CATEGORIES[0], description: "", urgency: "medium", contact_method: "secure_chat", confidential: true });
            load();
        } catch { toast.error("Could not submit request"); }
    };

    const shown = filter === "all" ? items : items.filter((i) => i.status === filter);

    return (
        <div className="space-y-6" data-testid="support-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Support Cases</h1>
                    <p className="text-slate-500 mt-1">{isMgr ? "Triage and manage confidential support cases." : "Your confidential support requests."}</p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-40 bg-white" data-testid="status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {["new", "triaged", "in_progress", "mediation", "resolved", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-nhs-blue hover:bg-nhs-darkblue text-white gap-2" data-testid="new-request-button"><Plus className="h-4 w-4" /> New request</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg" data-testid="new-request-dialog">
                            <DialogHeader><DialogTitle className="font-heading">Raise a confidential support request</DialogTitle></DialogHeader>
                            <div className="space-y-4 py-2">
                                <div>
                                    <Label>Subject</Label>
                                    <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Brief summary" className="mt-1.5" data-testid="request-subject-input" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Category</Label>
                                        <Select value={form.category} onValueChange={(v) => set("category", v)}>
                                            <SelectTrigger className="mt-1.5" data-testid="request-category-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Urgency</Label>
                                        <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                                            <SelectTrigger className="mt-1.5" data-testid="request-urgency-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>{URGENCY.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label>What's happening?</Label>
                                    <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the situation in confidence…" className="mt-1.5" data-testid="request-description-input" />
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5 text-nhs-teal" /> Handled confidentially by a culturally competent adviser.</div>
                            </div>
                            <DialogFooter>
                                <Button onClick={submit} className="bg-nhs-blue hover:bg-nhs-darkblue text-white" data-testid="submit-request-button">Submit request</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {shown.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400" data-testid="empty-cases">No cases found.</div>
            ) : (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
                    {shown.map((c) => (
                        <button key={c.id} onClick={() => navigate(`/app/support/${c.id}`)}
                            className="w-full text-left p-5 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-4" data-testid={`case-row-${c.id}`}>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-nhs-ink truncate">{c.subject}</span>
                                    <Badge className={`${urgencyBadge(c.urgency)} border-0 capitalize`}>{c.urgency}</Badge>
                                    <Badge className={`${statusBadge(c.status)} border-0`}>{c.status.replace("_", " ")}</Badge>
                                </div>
                                <p className="text-sm text-slate-500 mt-1 truncate">{c.category}{isMgr ? ` · ${c.requester_name}` : ""}</p>
                            </div>
                            <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
